import React, { useState, useEffect } from "react";

import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import GetAppIcon from "@material-ui/icons/GetApp";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import useUsers from "../../../hooks/useUsers";

// Relatorios - SOMENTE apresentacao. As linhas (incluindo "Conexao", SLA
// formatado, Atrasado e Observacao) chegam prontas de GET
// /solicitacoes/exportar. A tabela na tela e o arquivo .xlsx usam as MESMAS
// linhas carregadas em memoria, entao o Excel nunca difere do que esta sendo
// exibido. Nenhum calculo de SLA acontece aqui.
const VALID_STATUSES = [
	"created",
	"in_progress",
	"waiting_btg",
	"waiting_assessor_client",
	"btg_refused",
	"completed",
	"cancelled",
];

const EMPTY_FILTERS = {
	createdFrom: "",
	createdTo: "",
	status: "",
	typeId: "",
	attendantId: "",
	search: "",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const useStyles = makeStyles(theme => ({
	root: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		overflowX: "hidden",
		padding: theme.spacing(0.5),
		...(theme.scrollbarStyles || {}),
	},
	paper: {
		padding: theme.spacing(2),
		borderRadius: 12,
	},
	title: {
		fontWeight: 600,
	},
	hint: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	actions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginTop: theme.spacing(1),
	},
	resultsPaper: {
		marginTop: theme.spacing(1),
		borderRadius: 12,
	},
	resultsBar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 2),
	},
	tableWrap: {
		overflowX: "auto",
		...(theme.scrollbarStyles || {}),
	},
	cell: {
		whiteSpace: "nowrap",
	},
	cellObs: {
		minWidth: 260,
		maxWidth: 420,
	},
	centered: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3),
	},
}));

// <input type="date"> devolve "AAAA-MM-DD" (dia local). Enviado como
// instante ISO cobrindo o dia inteiro local - sem isso o backend leria a data
// como meia-noite UTC e o "ate" excluiria o proprio dia escolhido.
const startOfDayIso = value => {
	const [y, m, d] = value.split("-").map(Number);
	return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};
const endOfDayIso = value => {
	const [y, m, d] = value.split("-").map(Number);
	return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};

const todayStamp = () => {
	const now = new Date();
	const pad = n => String(n).padStart(2, "0");
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const RelatoriosPanel = () => {
	const classes = useStyles();
	const { users } = useUsers();
	const [types, setTypes] = useState([]);
	const [filters, setFilters] = useState(EMPTY_FILTERS);
	const [exporting, setExporting] = useState(false);
	const [loading, setLoading] = useState(false);
	const [report, setReport] = useState({ headers: [], rows: [], total: 0, truncated: false });
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	useEffect(() => {
		const fetchTypes = async () => {
			try {
				const { data } = await api.get("/solicitacoes/tipos");
				setTypes(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchTypes();
	}, []);

	const invalidPeriod =
		!!filters.createdFrom && !!filters.createdTo && filters.createdFrom > filters.createdTo;

	// Carrega sozinho a cada mudanca de filtro (debounce para a busca por texto).
	useEffect(() => {
		if (invalidPeriod) return undefined;

		let ignore = false;
		setLoading(true);
		const timer = setTimeout(async () => {
			try {
				const search = filters.search.trim();
				const onlyDigits = /^\d+$/.test(search);
				const { data } = await api.get("/solicitacoes/exportar", {
					params: {
						createdFrom: filters.createdFrom ? startOfDayIso(filters.createdFrom) : undefined,
						createdTo: filters.createdTo ? endOfDayIso(filters.createdTo) : undefined,
						status: filters.status || undefined,
						typeId: filters.typeId || undefined,
						attendantId: filters.attendantId || undefined,
						// Mesma regra da tela de Solicitacoes: so digitos = numero da
						// solicitacao; qualquer outra coisa = nome do cliente.
						number: onlyDigits ? search : undefined,
						search: search && !onlyDigits ? search : undefined,
					},
				});
				if (!ignore) {
					setReport(data);
					setPage(0);
				}
			} catch (err) {
				if (!ignore) toastError(err);
			} finally {
				if (!ignore) setLoading(false);
			}
		}, 400);

		return () => {
			ignore = true;
			clearTimeout(timer);
		};
	}, [filters, invalidPeriod]);

	const handleFilterChange = field => e =>
		setFilters(prev => ({ ...prev, [field]: e.target.value }));

	const handleExport = () => {
		if (!report.rows.length) {
			toast.info(i18n.t("minhasSolicitacoes.relatorios.semDados"));
			return;
		}

		setExporting(true);
		try {
			const sheet = XLSX.utils.aoa_to_sheet([report.headers, ...report.rows]);
			sheet["!cols"] = report.headers.map((header, index) => ({
				wch: Math.min(
					60,
					Math.max(
						header.length + 2,
						...report.rows.map(row => String(row[index] || "").length + 2)
					)
				),
			}));

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, sheet, "Solicitações");
			XLSX.writeFile(workbook, `solicitacoes_${todayStamp()}.xlsx`);
		} catch (err) {
			toastError(err);
		} finally {
			setExporting(false);
		}
	};

	const pagedRows = report.rows.slice(page * pageSize, page * pageSize + pageSize);

	return (
		<div id="relatorios-panel" className={classes.root}>
			<Paper className={classes.paper} variant="outlined">
				<Typography variant="subtitle1" className={classes.title}>
					{i18n.t("minhasSolicitacoes.relatorios.titulo")}
				</Typography>
				<Typography variant="body2" className={classes.hint}>
					{i18n.t("minhasSolicitacoes.relatorios.descricao")}
				</Typography>

				<Grid container spacing={1} alignItems="center">
					<Grid item xs={6} sm={2}>
						<TextField
							id="relatorios-filtro-periodo-de"
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.periodoDe")}
							type="date"
							fullWidth
							size="small"
							variant="outlined"
							InputLabelProps={{ shrink: true }}
							value={filters.createdFrom}
							onChange={handleFilterChange("createdFrom")}
						/>
					</Grid>
					<Grid item xs={6} sm={2}>
						<TextField
							id="relatorios-filtro-periodo-ate"
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.periodoAte")}
							type="date"
							fullWidth
							size="small"
							variant="outlined"
							InputLabelProps={{ shrink: true }}
							value={filters.createdTo}
							onChange={handleFilterChange("createdTo")}
						/>
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField
							id="relatorios-filtro-status"
							select
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.status")}
							fullWidth
							size="small"
							variant="outlined"
							value={filters.status}
							onChange={handleFilterChange("status")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.visaoGeral.filtros.statusTodos")}
							</MenuItem>
							{VALID_STATUSES.map(s => (
								<MenuItem key={s} value={s}>
									{i18n.t(`solicitacoes.status.${s}`)}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField
							id="relatorios-filtro-demanda"
							select
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.demanda")}
							fullWidth
							size="small"
							variant="outlined"
							value={filters.typeId}
							onChange={handleFilterChange("typeId")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.visaoGeral.filtros.demandaTodas")}
							</MenuItem>
							{types.map(t => (
								<MenuItem key={t.id} value={t.id}>
									{t.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField
							id="relatorios-filtro-atendente"
							select
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.atendente")}
							fullWidth
							size="small"
							variant="outlined"
							value={filters.attendantId}
							onChange={handleFilterChange("attendantId")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.visaoGeral.filtros.atendenteTodos")}
							</MenuItem>
							{users.map(u => (
								<MenuItem key={u.id} value={String(u.id)}>
									{u.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							id="relatorios-filtro-busca"
							label={i18n.t("minhasSolicitacoes.relatorios.busca")}
							fullWidth
							size="small"
							variant="outlined"
							value={filters.search}
							onChange={handleFilterChange("search")}
						/>
					</Grid>
				</Grid>

				<div className={classes.actions}>
					<Button
						id="relatorios-limpar-btn"
						size="small"
						onClick={() => setFilters(EMPTY_FILTERS)}
						disabled={loading}
					>
						{i18n.t("minhasSolicitacoes.visaoGeral.filtros.limpar")}
					</Button>
					{invalidPeriod && (
						<Typography variant="caption" color="error">
							{i18n.t("minhasSolicitacoes.relatorios.periodoInvalido")}
						</Typography>
					)}
				</div>
			</Paper>

			<Paper className={classes.resultsPaper} variant="outlined">
				<div className={classes.resultsBar}>
					<Typography variant="body2" color="textSecondary" id="relatorios-contagem">
						{loading
							? i18n.t("minhasSolicitacoes.relatorios.carregando")
							: i18n.t("minhasSolicitacoes.relatorios.exibindo", {
									exibidos: pagedRows.length,
									total: report.rows.length,
							  })}
						{!loading && report.truncated
							? ` · ${i18n.t("minhasSolicitacoes.relatorios.truncadoAviso", {
									exportadas: report.rows.length,
									total: report.total,
							  })}`
							: ""}
					</Typography>
					<Button
						id="relatorios-exportar-xlsx-btn"
						variant="contained"
						color="primary"
						startIcon={
							exporting ? <CircularProgress size={18} color="inherit" /> : <GetAppIcon />
						}
						onClick={handleExport}
						disabled={exporting || loading || !report.rows.length}
					>
						{i18n.t("minhasSolicitacoes.relatorios.exportar")}
					</Button>
				</div>

				<div className={classes.tableWrap}>
					<Table size="small" id="relatorios-tabela">
						<TableHead>
							<TableRow>
								{report.headers.map(header => (
									<TableCell key={header} className={classes.cell}>
										<strong>{header}</strong>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{pagedRows.map((row, rowIndex) => (
								// eslint-disable-next-line react/no-array-index-key
								<TableRow key={`${page}-${rowIndex}`} hover>
									{row.map((value, colIndex) => (
										<TableCell
											// eslint-disable-next-line react/no-array-index-key
											key={colIndex}
											className={
												colIndex === row.length - 1 ? classes.cellObs : classes.cell
											}
										>
											{value}
										</TableCell>
									))}
								</TableRow>
							))}
							{!loading && report.rows.length === 0 && (
								<TableRow>
									<TableCell colSpan={report.headers.length || 12} align="center">
										{i18n.t("minhasSolicitacoes.relatorios.semDados")}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
					{loading && (
						<div className={classes.centered}>
							<CircularProgress size={28} />
						</div>
					)}
				</div>

				<TablePagination
					component="div"
					count={report.rows.length}
					page={page}
					onChangePage={(e, newPage) => setPage(newPage)}
					rowsPerPage={pageSize}
					onChangeRowsPerPage={e => {
						setPageSize(Number(e.target.value));
						setPage(0);
					}}
					rowsPerPageOptions={PAGE_SIZE_OPTIONS}
					labelRowsPerPage={i18n.t("minhasSolicitacoes.relatorios.porPagina")}
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
				/>
			</Paper>
		</div>
	);
};

export default RelatoriosPanel;
